/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {useEffect, useState} from 'react';

// defines the row model types...
export enum RowModel {
  Infinite = 'infinite',
  ClientSide = 'clientSide'
};

// helper method helps to determine  model type...
export const isSameRowModel = (model: RowModel, value: RowModel) => model === value;

// helper method helps to determine client side model type...
export const isClientSideRowModel = (model: RowModel) => isSameRowModel(RowModel.ClientSide, model);

// helper method helps to determine infinite model type...
export const isInfiniteRowModel = (model: RowModel) => isSameRowModel(RowModel.Infinite, model);

/**
 * Custom hook returns an object which contains the boolean properties that
 * are isClientModel and isInfiniteModel. isInfiniteModel provides the whether
 * the model is infinite or not in the form of a boolean value, whereas isClientModel
 * do the same as the isInfiniteModel property.
 *
 * ```typescript
 *
 *  import {useRowModel} from '@ion/dls/src/grid/';
 *
 *  const {isClientModel, isInfiniteModel} = useRowModel(model);
 *
 * ```
 */
export const useRowModel = (model) => {
  const [{isClientModel, isInfiniteModel}, setRowModelType] = useState({
    isClientModel   : isClientSideRowModel(model),
    isInfiniteModel : isInfiniteRowModel(model)
  });

  useEffect(() => {
    setRowModelType({
      isClientModel   : isClientSideRowModel(model),
      isInfiniteModel : isInfiniteRowModel(model)
    });
  }, [model]);

  return {isClientModel, isInfiniteModel};
};

export default useRowModel;
